var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { Navigate } from 'react-router-dom';
import { UXPContext } from "@core/context/Context";
import { getLogo, hasValue } from "@utils";
import { BaseLayout } from "@core/layout/BaseLayout";
import { processNavigationLinks, convertDashboardSidebarToTopNavigation } from "@core/layout/navigationUtils";
import { useRoutes } from "@core/layout/useRoutes";
import { NotAuthorized, NotFound } from "@core/layout/components/ErrorPages";
import { BaseLayoutSkeleton } from "@core/layout/BaseLayoutSkeleton";
import ConfiguredPage from "@core/views/configured-page/ConfiguredPage";
import { SkeletonLoader } from "@widget/loaders";
import DashboardView from "./dashboard-view/DashboardView";
import { EmbeddedDashboard } from "@core/dashboard/embedded-dashboard/EmbeddedDashboard";
import FloatingActions from "@core/floating-actions/FloatingActions";
import AuthLayout from "@core/auth/layout/AuthLayout";
import { useEventSubscriber } from "@realtime/eventbus/useEventBus";
import { getGlobalDashboardSettings } from "@core/services/services";
const App = () => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2;
    const context = useContext(UXPContext);
    const { matchedRoute, isAllowed, isDashboard, isLoading, isAnonymous } = useRoutes();
    const [expandedSidebar, setExpandedSidebar] = useState(true);
    const [navEventProps, setNavEventProps] = useState({});
    const [globalDashboardSettings, setGlobalDashboardSettings] = useState(null);
    useEffect(() => {
        getGlobalDashboardSettings(context).then(settings => {
            if (settings)
                setGlobalDashboardSettings(settings);
        });
    }, []);
    useEffect(() => { setNavEventProps({}); }, [matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.matchedPath]);
    /*
     * Listen for global navigation events (e.g. digital twin view changes) and merge into widget props.
     * instanceId is stripped — it's an event bus implementation detail, not a widget prop.
     */
    const handleNavEvent = useCallback((payload) => {
        if (payload) {
            const _a = payload, { instanceId: _ } = _a, rest = __rest(_a, ["instanceId"]);
            setNavEventProps(rest);
        }
    }, []);
    useEventSubscriber('uxp-app', 'dt-view-change-event', handleNavEvent);
    /*
     * Merge URL path params, query params, and event-driven props into a single object
     * passed to all rendered widgets so they can react to the current navigation context.
     */
    const globalProps = useMemo(() => (Object.assign(Object.assign(Object.assign({}, ((matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.params) || {})), ((matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.query) || {})), navEventProps)), [matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.params, matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.query, navEventProps]);
    const [navigationLinks, setNavigationLinks] = useState({ sidebar: [], top: {}, all: [] });
    const [loadingNavigation, setLoadingNavigation] = useState(false);
    const processSidebarItems = useCallback(() => {
        var _a, _b;
        setLoadingNavigation(true);
        const navigation = processNavigationLinks(context, isAnonymous);
        const dashboardNavigation = isDashboard && ((_a = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _a === void 0 ? void 0 : _a.dashboarConfig)
            ? convertDashboardSidebarToTopNavigation((_b = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _b === void 0 ? void 0 : _b.dashboarConfig)
            : {};
        /*
         * When on a dashboard route, show only the dashboard's own navigation so the
         * dashboard title and sidebar sections render correctly. Fall back to the
         * regular app navigation on all other routes.
         */
        const mergedTopNavigation = isDashboard && Object.keys(dashboardNavigation).length > 0
            ? dashboardNavigation
            : navigation.top;
        const dashboardLinks = [];
        Object.values(dashboardNavigation).forEach(topNav => {
            var _a;
            dashboardLinks.push(topNav.link);
            if ((_a = topNav.children) === null || _a === void 0 ? void 0 : _a.length) {
                const flattenChildren = (children) => {
                    children.forEach(child => {
                        var _a;
                        dashboardLinks.push(child);
                        if ((_a = child.children) === null || _a === void 0 ? void 0 : _a.length)
                            flattenChildren(child.children);
                    });
                };
                flattenChildren(topNav.children);
            }
        });
        setNavigationLinks(Object.assign(Object.assign({}, navigation), { top: mergedTopNavigation, all: [...navigation.all, ...dashboardLinks] }));
        setLoadingNavigation(false);
    }, [context, isDashboard, (_a = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _a === void 0 ? void 0 : _a.dashboarConfig, isAnonymous]);
    useEffect(() => { processSidebarItems(); }, [processSidebarItems]);
    useEffect(() => {
        var _a;
        const parts = [(_a = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _a === void 0 ? void 0 : _a.title, context.accountDisplayName].filter(p => hasValue(p));
        if (parts.length > 0)
            document.title = parts.join(' | ');
    }, [matchedRoute, context.accountDisplayName]);
    useEffect(() => {
        document.documentElement.setAttribute('data-uxp-sidebar', expandedSidebar ? 'expanded' : 'collapsed');
    }, [expandedSidebar]);
    const handleSidebarToggle = useCallback(() => {
        setExpandedSidebar(prev => !prev);
    }, []);
    const renderSiteImage = useCallback(() => {
        if (isAnonymous)
            return null;
        if (!hasValue(context === null || context === void 0 ? void 0 : context.userKey))
            return _jsx(SkeletonLoader, { width: "4rem", height: "4rem", additionalStyle: { borderRadius: '0.8rem' } });
        if (hasValue(context === null || context === void 0 ? void 0 : context.userSiteImageKey))
            return _jsx("div", { className: "uxpcore_usersite__image", style: { backgroundImage: `url(/Attachments/Download?AttachmentKey=${context.userSiteImageKey})` }, onClick: () => {
                    if (!hasValue(context === null || context === void 0 ? void 0 : context.userSiteKey))
                        return;
                    window.open(`/view/location/details/${context === null || context === void 0 ? void 0 : context.userSiteKey}`);
                } });
        return null;
    }, [context]);
    if (isLoading)
        return _jsx(BaseLayoutSkeleton, {});
    if (!isAllowed) {
        // Anonymous user hitting a non-public route — redirect to login
        if (isAnonymous)
            return _jsx(Navigate, { to: "/view/auth/login", replace: true });
        return _jsx(NotAuthorized, {});
    }
    if (!matchedRoute)
        return _jsx(NotFound, {});
    const hasTOS = context === null || context === void 0 ? void 0 : context.hasTOS;
    const tosAgreed = context === null || context === void 0 ? void 0 : context.tosAgreed;
    const hasAgrement = context === null || context === void 0 ? void 0 : context.hasAgreement;
    const agreementAgreed = context === null || context === void 0 ? void 0 : context.agreementAgreed;
    const currentPath = window.location.pathname.toLowerCase();
    const isTOSPage = currentPath.includes('/view/system/tos');
    const isAgreementPage = currentPath.includes('/view/system/agreement');
    // Only enforce full-screen TOS when TOS is actually configured (hasTOS=true) AND not agreed
    if (hasTOS && isTOSPage && !tosAgreed) {
        return _jsx(AuthLayout, { children: _jsx(ConfiguredPage, { pageId: (_b = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _b === void 0 ? void 0 : _b.pageId, additionalProps: globalProps, configuredProps: (_c = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _c === void 0 ? void 0 : _c.configuredProps }, (_d = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _d === void 0 ? void 0 : _d.pageId) });
    }
    // Agreement enforcement: render agreement page without navigation if not agreed (only check if TOS agreed)
    if (hasAgrement && isAgreementPage && tosAgreed && !agreementAgreed) {
        return _jsx(AuthLayout, { children: _jsx(ConfiguredPage, { pageId: (_e = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _e === void 0 ? void 0 : _e.pageId, additionalProps: globalProps, configuredProps: (_f = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _f === void 0 ? void 0 : _f.configuredProps }, (_g = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _g === void 0 ? void 0 : _g.pageId) });
    }
    return (_jsxs(BaseLayout, { header: {
            logo: getLogo(context),
            expandedSidebar: expandedSidebar,
            onToggleSidebar: handleSidebarToggle,
            navigationLinks: navigationLinks,
            renderRightSection: _jsx(_Fragment, {})
        }, sidebar: {
            navigationLinks: navigationLinks,
            loading: loadingNavigation,
            tools: {
                search: { enable: false, onClick: () => { } },
                marketPlace: { enable: false, url: 'https://spaceworx.com' },
                renderTools: renderSiteImage()
            }
        }, children: [isDashboard
                ? _jsx(DashboardView, { details: (_j = (_h = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _h === void 0 ? void 0 : _h.dashboarConfig) === null || _j === void 0 ? void 0 : _j.details, widgetConfiguration: (_l = (_k = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _k === void 0 ? void 0 : _k.dashboarConfig) === null || _l === void 0 ? void 0 : _l.widgetConfiguration, subDashboardId: ((_o = (_m = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _m === void 0 ? void 0 : _m.dashboarConfig) === null || _o === void 0 ? void 0 : _o.subDashboardId) || 'default', globalProps: globalProps, globalDashboardSettings: globalDashboardSettings })
                : ((_p = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _p === void 0 ? void 0 : _p.type) === 'embeddedDashboard'
                    ? _jsx(EmbeddedDashboard, { ids: ((_r = (_q = matchedRoute.config.metadata) === null || _q === void 0 ? void 0 : _q.embeddedDashboard) === null || _r === void 0 ? void 0 : _r.link) ? [matchedRoute.config.metadata.embeddedDashboard.link] : [], overlayMode: (_t = (_s = matchedRoute.config.metadata) === null || _s === void 0 ? void 0 : _s.embeddedDashboard) === null || _t === void 0 ? void 0 : _t.overlayMode, enableBackgroundConfig: (_v = (_u = matchedRoute.config.metadata) === null || _u === void 0 ? void 0 : _u.embeddedDashboard) === null || _v === void 0 ? void 0 : _v.enableBackgroundConfig, enableUserGroupLayouts: (_x = (_w = matchedRoute.config.metadata) === null || _w === void 0 ? void 0 : _w.embeddedDashboard) === null || _x === void 0 ? void 0 : _x.enableUserGroupLayouts, enableResponsiveLayouts: (_z = (_y = matchedRoute.config.metadata) === null || _y === void 0 ? void 0 : _y.embeddedDashboard) === null || _z === void 0 ? void 0 : _z.enableResponsiveLayouts, allowToConfigure: true, autoPassedProps: globalProps, globalDashboardSettings: globalDashboardSettings })
                    : _jsx(ConfiguredPage, { pageId: (_0 = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _0 === void 0 ? void 0 : _0.pageId, additionalProps: globalProps, configuredProps: (_1 = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _1 === void 0 ? void 0 : _1.configuredProps }, (_2 = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _2 === void 0 ? void 0 : _2.pageId), _jsx(FloatingActions, {})] }));
};
export default memo(App);
